import { useState, useEffect } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faShieldHalved,
  faCheck,
  faUser,
  faLock,
  faShield,
  faPlus,
  faPen,
  faTrash,
  faEye,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useParams } from "react-router-dom";

// ─────────────────────────────────────────────────────────────
// Permission Groups
// ─────────────────────────────────────────────────────────────

const permissionTabs = [
  "Capital",
  "Sales",
  "Purchase",
  "Business",
  "Payments",
  "Sales Capital",
  "Product",
  "Others",
];

const productTabs = ["Product Types", "Product Categories"];

// ─────────────────────────────────────────────────────────────
// CRUD Template
// ─────────────────────────────────────────────────────────────

const defaultCrud = {
  all_access: false,
  add: false,
  update: false,
  delete: false,
  view: false,
};

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────

const CustomerPermissions = () => {
  const { id } = useParams();
  const [customerName, setCustomerName] = useState("Loading...");

  const [permissions, setPermissions] = useState({
    Capital: { ...defaultCrud },
    Sales: { ...defaultCrud },
    Purchase: { ...defaultCrud },
    Business: { ...defaultCrud },
    Payments: { ...defaultCrud },
    "Sales Capital": { ...defaultCrud },
    Others: { ...defaultCrud },
    Product: {
      "Product Types": { ...defaultCrud },
      "Product Categories": { ...defaultCrud },
    },
  });

  // Simulate loading customer name
  useEffect(() => {
    // In real app, fetch customer data using id
    const mockCustomers = {
      1: "Amina Hassan Rashid",
      2: "Juma Selemani Kondo",
      3: "Fatuma Ali Mwenda",
    };
    setCustomerName(mockCustomers[id] || `Customer #${id}`);
  }, [id]);

  // ─────────────────────────────────────────────────────────────
  // Toggle Normal Permission
  // ─────────────────────────────────────────────────────────────

  const togglePermission = (tab, key, value) => {
    setPermissions((prev) => {
      const updated = {
        ...prev,
        [tab]: {
          ...prev[tab],
          [key]: value,
        },
      };
      return updated;
    });
  };

  // ─────────────────────────────────────────────────────────────
  // Toggle Product Permission
  // ─────────────────────────────────────────────────────────────

  const toggleProductPermission = (productTab, key, value) => {
    setPermissions((prev) => ({
      ...prev,
      Product: {
        ...prev.Product,
        [productTab]: {
          ...prev.Product[productTab],
          [key]: value,
        },
      },
    }));
  };

  // ─────────────────────────────────────────────────────────────
  // All Access Logic
  // ─────────────────────────────────────────────────────────────

  const handleAllAccess = (tab, value) => {
    setPermissions((prev) => ({
      ...prev,
      [tab]: {
        all_access: value,
        add: value,
        update: value,
        delete: value,
        view: value,
      },
    }));
  };

  const handleProductAllAccess = (productTab, value) => {
    setPermissions((prev) => ({
      ...prev,
      Product: {
        ...prev.Product,
        [productTab]: {
          all_access: value,
          add: value,
          update: value,
          delete: value,
          view: value,
        },
      },
    }));
  };

  // ─────────────────────────────────────────────────────────────
  // Permission Card
  // ─────────────────────────────────────────────────────────────

  const PermissionCard = ({ title, data, onToggle, onAllAccess }) => {
    const permissionItems = [
      {
        key: "add",
        label: "Add",
        icon: faPlus,
        color: "green",
        bgColor: "bg-green-50",
        textColor: "text-green-700",
        borderColor: "border-green-200",
        description: "Can create new records",
      },
      {
        key: "update",
        label: "Update",
        icon: faPen,
        color: "blue",
        bgColor: "bg-blue-50",
        textColor: "text-blue-700",
        borderColor: "border-blue-200",
        description: "Can edit existing records",
      },
      {
        key: "delete",
        label: "Delete",
        icon: faTrash,
        color: "red",
        bgColor: "bg-red-50",
        textColor: "text-red-700",
        borderColor: "border-red-200",
        description: "Can remove records",
      },
      {
        key: "view",
        label: "View",
        icon: faEye,
        color: "purple",
        bgColor: "bg-purple-50",
        textColor: "text-purple-700",
        borderColor: "border-purple-200",
        description: "Can view records",
      },
    ];

    return (
      <Card className="border border-gray-200 shadow-sm overflow-hidden">
        {/* Card Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-white border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faShieldHalved}
                  className="text-blue-800 h-4 w-4"
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                <p className="text-sm text-gray-600">
                  Manage user permissions for this section
                </p>
              </div>
            </div>
            <Badge className="bg-blue-800 text-white hover:bg-blue-900 px-3 py-1">
              <FontAwesomeIcon icon={faShield} className="mr-1.5 h-3 w-3" />
              Permissions
            </Badge>
          </div>
        </div>

        {/* Card Body */}
        <div className="p-6 space-y-3">
          {/* All Access */}
          <div className="flex items-center justify-between bg-gray-50 rounded-xl px-5 py-4 border border-gray-200 hover:border-blue-300 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faLock}
                  className="text-blue-700 h-4 w-4"
                />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">All Access</h4>
                <p className="text-sm text-gray-600">
                  Enable or disable all permissions at once
                </p>
              </div>
            </div>
            <Switch
              checked={data.all_access}
              onCheckedChange={onAllAccess}
              className="data-[state=checked]:bg-blue-800"
            />
          </div>

          {/* Permission Items */}
          {permissionItems.map((item) => (
            <div
              key={item.key}
              className={`flex items-center justify-between rounded-xl px-5 py-4 border transition-all duration-200 ${
                data[item.key]
                  ? `${item.bgColor} ${item.borderColor} shadow-sm`
                  : "bg-white border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`w-10 h-10 rounded-full ${item.bgColor} flex items-center justify-center`}
                >
                  <FontAwesomeIcon
                    icon={item.icon}
                    className={`h-4 w-4 ${item.textColor}`}
                  />
                </div>
                <div>
                  <h4
                    className={`font-semibold ${data[item.key] ? item.textColor : "text-gray-900"}`}
                  >
                    {item.label}
                  </h4>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              </div>
              <Switch
                checked={data[item.key]}
                onCheckedChange={(value) => onToggle(item.key, value)}
                className="data-[state=checked]:bg-blue-800"
              />
            </div>
          ))}
        </div>
      </Card>
    );
  };

  // ─────────────────────────────────────────────────────────────
  // Save Button Handler
  // ─────────────────────────────────────────────────────────────

  const handleSavePermissions = () => {
    // In real app, save permissions to backend
    console.log("Saving permissions:", permissions);
    // Show success toast or notification
    alert("Permissions saved successfully!");
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <Card className="bg-white border border-gray-200 px-4 sm:px-6 py-4 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Button
              asChild
              variant="outline"
              className="border-gray-300 hover:border-blue-800 hover:bg-blue-50"
            >
              <Link to="/app/customers/">
                <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
              </Link>
            </Button>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-800 to-blue-600 flex items-center justify-center shadow-md">
                <FontAwesomeIcon icon={faUser} className="text-white h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
                  Customer Permissions
                </h2>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-gray-600">
                    Managing permissions for
                  </p>
                  <span className="text-sm font-semibold text-blue-800">
                    {customerName}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <Button
            onClick={handleSavePermissions}
            className="gap-2 bg-blue-800 hover:bg-blue-900 text-white shadow-sm px-6"
          >
            <FontAwesomeIcon icon={faCheck} className="h-4 w-4" />
            Save Permissions
          </Button>
        </div>
      </Card>

      {/* Main Tabs */}
      <Card className="bg-white border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={faLayerGroup}
              className="text-blue-800 h-4 w-4"
            />
            <span className="text-sm font-medium text-gray-700">
              Permission Groups
            </span>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <Tabs defaultValue="Capital" className="w-full">
            <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0 mb-6">
              {permissionTabs.map((tab) => (
                <TabsTrigger
                  key={tab}
                  value={tab}
                  className={`border border-gray-200 data-[state=active]:border-blue-800 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-800 data-[state=active]:shadow-sm px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200`}
                >
                  {tab}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Normal Tabs */}
            {permissionTabs
              .filter((tab) => tab !== "Product")
              .map((tab) => (
                <TabsContent key={tab} value={tab} className="mt-0">
                  <PermissionCard
                    title={tab}
                    data={permissions[tab]}
                    onToggle={(key, value) => togglePermission(tab, key, value)}
                    onAllAccess={(value) => handleAllAccess(tab, value)}
                  />
                </TabsContent>
              ))}

            {/* Product Tab */}
            <TabsContent value="Product" className="mt-0">
              <div className="mb-6">
                <Tabs defaultValue="Product Types" className="w-full">
                  <TabsList className="flex gap-2 h-auto bg-transparent p-0">
                    {productTabs.map((tab) => (
                      <TabsTrigger
                        key={tab}
                        value={tab}
                        className={`border border-gray-200 data-[state=active]:border-blue-800 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-800 data-[state=active]:shadow-sm px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200`}
                      >
                        {tab}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {/* Product Types */}
                  <TabsContent value="Product Types" className="mt-4">
                    <PermissionCard
                      title="Product Types"
                      data={permissions.Product["Product Types"]}
                      onToggle={(key, value) =>
                        toggleProductPermission("Product Types", key, value)
                      }
                      onAllAccess={(value) =>
                        handleProductAllAccess("Product Types", value)
                      }
                    />
                  </TabsContent>

                  {/* Product Categories */}
                  <TabsContent value="Product Categories" className="mt-4">
                    <PermissionCard
                      title="Product Categories"
                      data={permissions.Product["Product Categories"]}
                      onToggle={(key, value) =>
                        toggleProductPermission(
                          "Product Categories",
                          key,
                          value,
                        )
                      }
                      onAllAccess={(value) =>
                        handleProductAllAccess("Product Categories", value)
                      }
                    />
                  </TabsContent>
                </Tabs>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </Card>

      {/* Footer Stats */}
      <Card className="bg-white border border-gray-200 shadow-sm px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>
              <span className="font-semibold text-gray-700">
                {Object.values(permissions).reduce((acc, val) => {
                  if (typeof val === "object" && val.all_access !== undefined) {
                    return acc + (val.all_access ? 1 : 0);
                  }
                  return acc;
                }, 0)}
              </span>{" "}
              sections have full access
            </span>
            <span className="hidden sm:inline text-gray-300">|</span>
            <span>
              <span className="font-semibold text-gray-700">
                {Object.values(permissions).reduce((acc, val) => {
                  if (typeof val === "object" && val.all_access !== undefined) {
                    return (
                      acc + Object.values(val).filter((v) => v === true).length
                    );
                  }
                  return acc;
                }, 0)}
              </span>{" "}
              total permissions enabled
            </span>
          </div>
          <div className="text-xs text-gray-600">
            Last updated: {new Date().toLocaleString()}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CustomerPermissions;
